import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, ButtonGroup, Card, CardBody, Col, Row } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ErrorToaster, successToaster, warningToaster } from "components/common/toast";
import { GetAgendaAPI, GetPendingBookingsAPI, GetRoomsAPI, GetWeekAPI, SetBookingAPI } from "network/bookings";
import BookingModal from "./BookingModal";
import TableSelect from "./TableSelect";
import { errText } from "./errText";
import {
  FILTERS, actionsFor, addDays, channelNames, faded, fill, filterBookings, groupByShift, pauseUntil, quickDays,
  statusKey, tableGroups, weekdayOf,
} from "./agendaLogic";

export { errText };

// Colori degli stati (spec): In attesa arancione, Confermata verde, Arrivati azzurro, No-show rosso, Cancellata grigio.
// `cls` resta per chi usa le classi badge di bootstrap (Floor.js).
export const STATUS = {
  pending: { cls: "warning", color: "#e67e22" },
  confirmed: { cls: "success", color: "#2e7d32" },
  arrived: { cls: "info", color: "#3fa7d6" },
  no_show: { cls: "danger", color: "#d9534f" },
  cancelled: { cls: "secondary", color: "#9e9e9e" },
  declined: { cls: "secondary", color: "#9e9e9e" },
};

const GOLD = "#b8932f";
const ORANGE = "#e67e22";
const SETTINGS_PATH = "/bookings/settings"; // le pagine del pannello sono montate alla radice (App.js)
const today = () => new Date().toLocaleDateString("sv-SE"); // AAAA-MM-GG locale
const EMPTY_DATA = { results: [], covers: 0, count: 0, active_count: 0, cancelled_count: 0, shifts: [], closed: false, auto_confirm: false, channels: [], pause: null, closure: null };

// Avvisa il resto dell'app (contatore del menu) che le prenotazioni sono cambiate.
export const notifyChanged = () => window.dispatchEvent(new Event("bookings:changed"));

// Barra di riempimento (massimo 100%).
const Bar = ({ covers, capacity }) => {
  const f = fill(covers, capacity);
  return (
    <div style={{ background: "rgba(0,0,0,0.08)", height: 6, borderRadius: 3, overflow: "hidden" }}>
      <div style={{ width: `${f.pct}%`, height: "100%", background: f.over ? STATUS.no_show.color : GOLD }} />
    </div>
  );
};

const Agenda = () => {
  const { t, i18n } = useTranslation("bookings");
  const navigate = useNavigate();
  const [date, setDate] = useState(today());
  const [mode, setMode] = useState("day");
  const [band, setBand] = useState("all");
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(EMPTY_DATA);
  const [week, setWeek] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [pendingDays, setPendingDays] = useState({});
  const [modal, setModal] = useState(null); // {booking}
  const latest = useRef(date);
  const todayStr = today();
  const quick = useMemo(() => quickDays(todayStr), [todayStr]);

  const load = useCallback(async () => {
    latest.current = date;
    try {
      const day = await GetAgendaAPI(date);
      if (latest.current === date) setData({ ...EMPTY_DATA, ...day });
      if (mode === "week") {
        const w = await GetWeekAPI(date);
        if (latest.current === date) setWeek(w);
      }
    } catch (e) {
      ErrorToaster(errText(e));
    }
  }, [date, mode]);
  useEffect(() => {
    load();
  }, [load]);

  // pallini dei giorni rapidi
  const loadPending = useCallback(() => {
    GetPendingBookingsAPI(quick[0].date, quick[quick.length - 1].date)
      .then((r) => setPendingDays(r?.days || {}))
      .catch(() => {});
  }, [quick]);
  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    GetRoomsAPI().then((r) => setRooms(Array.isArray(r) ? r : [])).catch(() => {});
  }, []);
  const groupsOfTables = useMemo(() => tableGroups(rooms), [rooms]);

  const changed = () => {
    load();
    loadPending();
    notifyChanged();
  };
  const goDay = (d) => {
    if (!d) return;
    setDate(d);
    setMode("day");
  };

  const joinList = (names) =>
    names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")}${t("agenda.and")}${names[names.length - 1]}`;
  // "Email di conferma inviata." (vuoto se non è partito nulla)
  const sentText = (notified, kind) => {
    const names = channelNames(notified);
    return names.length ? t(`agenda.sent.${kind}`, { count: names.length, channels: joinList(names) }) : "";
  };
  const toast = (ok, r, kind) => {
    const text = [ok, sentText(r?.notified, kind)].filter(Boolean).join(" ");
    if (r?.warning) warningToaster(`${text} ${r.warning}`);
    else successToaster(text);
  };

  const patch = async (b, payload, ok, kind = "confirm") => {
    try {
      const r = await SetBookingAPI(b.id, payload);
      toast(ok, r, kind);
      changed();
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  const act = (b, a) => patch(b, { status: a.to }, t(`agenda.done.${a.key}`, { name: b.name }), a.to === "cancelled" ? "cancel" : "confirm");
  const setTable = (b, id) => {
    if (id === "__multi") return;
    const tbl = groupsOfTables.flatMap((g) => g.tables).find((x) => x.id === id);
    patch(b, { tables: id ? [id] : [] }, t("agenda.tableSaved", { name: b.name, table: tbl ? tbl.name : t("agenda.noTable") }));
  };

  const step = mode === "week" ? 7 : 1;
  const locale = (i18n.language || "it").startsWith("en") ? "en-GB" : "it-IT";
  const dateLabel = new Date(date + "T12:00:00").toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const dayName = (d) => t(`days.${weekdayOf(d)}`);
  const quickLabel = (q) => (q.key === "weekday" ? `${dayName(q.date)} ${Number(q.date.slice(8))}` : t(`agenda.quick.${q.key}`));

  const shifts = data.shifts || [];
  const bandFilter = shifts.some((s) => s.id === band) ? band : "all";
  const pendingCount = data.results.filter((b) => b.status === "pending").length;
  const groups = groupByShift(filterBookings(data.results, filter), shifts).filter((g) => bandFilter === "all" || (g.shift && g.shift.id === bandFilter));

  // ---------- barra di avviso (solo oggi) ----------
  const banner = () => {
    if (date !== todayStr) return null;
    const p = data.pause && data.pause.active ? data.pause : null;
    if (!p && !data.closure) return null;
    let title;
    let lines = [];
    if (p) {
      const names = shifts.filter((s) => (p.shifts || []).includes(s.id)).map((s) => s.name);
      title = t("agenda.pauseTitle", { services: names.length ? names.join(", ") : t("agenda.allServices") });
      const u = pauseUntil(p.until);
      lines = [p.message, u && (u.midnight ? t("agenda.reopenTomorrow") : t("agenda.retryAfter", { time: u.time }))];
    } else {
      title = data.closure.title;
      lines = [data.closure.message];
    }
    return (
      <div className="d-flex flex-wrap align-items-center mb-3 p-3" style={{ background: "#fff3e0", borderLeft: `4px solid ${ORANGE}`, borderRadius: 4, color: "#7a4100" }}>
        <div className="mr-3" style={{ flex: 1, minWidth: 220 }}>
          <strong>{title}</strong>
          {lines.filter(Boolean).map((l, i) => <div key={i} className="small">{l}</div>)}
        </div>
        <Button size="sm" style={{ background: ORANGE, borderColor: ORANGE }} onClick={() => navigate(SETTINGS_PATH)}>{t("agenda.manageInSettings")}</Button>
      </div>
    );
  };

  // ---------- riquadri riepilogo ----------
  const tile = (key, label, value, sub, color) => (
    <Col key={key} xs="6" md="3" xl className="mb-3">
      <Card className="mb-0 h-100">
        <CardBody className="p-3">
          <div className="small text-muted">{label}</div>
          <div style={{ fontSize: 26, fontWeight: 600, color }}>{value}</div>
          {sub}
        </CardBody>
      </Card>
    </Col>
  );
  const tiles = (
    <Row>
      {tile("count", t("agenda.tiles.bookings"), data.active_count, <div className="small text-muted">{t("agenda.tiles.cancelled", { count: data.cancelled_count })}</div>)}
      {shifts.map((s) =>
        tile(
          s.id,
          t("agenda.tiles.covers", { name: s.name.toLowerCase(), from: s.start_time, to: s.end_time }),
          <>{s.covers} <span className="text-muted" style={{ fontSize: 16 }}>/ {s.capacity}</span></>,
          s.closed ? <div className="small text-muted">{t("agenda.closed")}</div> : <Bar covers={s.covers} capacity={s.capacity} />
        )
      )}
      {tile(
        "pending",
        t("agenda.tiles.pending"),
        pendingCount,
        <div className="small text-muted">{t(data.auto_confirm ? "agenda.tiles.autoOn" : "agenda.tiles.autoOff")}</div>,
        pendingCount > 0 ? ORANGE : undefined
      )}
    </Row>
  );

  // ---------- riga prenotazione ----------
  const row = (b) => {
    const st = STATUS[b.status] || STATUS.confirmed;
    const tables = b.tables || [];
    const tableValue = tables.length > 1 ? "__multi" : tables[0] ? tables[0].id : "";
    const notes = [b.occasion, b.notes].filter(Boolean).join(" · ");
    return (
      <Row key={b.id} className="booking-row align-items-center py-2 border-bottom" style={{ opacity: faded(b.status) ? 0.5 : 1 }}>
        <Col xs="3" md="1"><span style={{ fontSize: 20, fontWeight: 600 }}>{b.time}</span></Col>
        <Col xs="9" md="3">
          <strong>{b.name}</strong>{" "}
          <span className="badge" style={{ border: `1px solid ${GOLD}`, color: GOLD, fontWeight: 500 }}>{t(`source.${b.source}`, { defaultValue: b.source })}</span>
          <div className="small text-muted">{b.phone}</div>
        </Col>
        <Col xs="4" md="1">{t("agenda.persons", { count: b.party_size })}</Col>
        <Col xs="8" md="2">
          <TableSelect bsSize="sm" groups={groupsOfTables} current={tables} value={tableValue} onChange={(id) => setTable(b, id)} aria-label={t("modal.table")} />
        </Col>
        <Col xs="12" md="2" className="small">
          {b.allergies && <div className="text-danger font-weight-bold">{t("agenda.allergies", { text: b.allergies })}</div>}
          {notes && <div>{notes}</div>}
          {b.calls?.length > 0 && <div className="text-muted" title={b.calls[0].summary}>{t("agenda.callSummary")}</div>}
        </Col>
        <Col xs="12" md="3" className="text-md-right mt-2 mt-md-0">
          <span className="badge mr-1" style={{ background: st.color, color: "#fff" }}>{t(`status.${statusKey(b.status)}`, { defaultValue: b.status })}</span>
          {actionsFor(b.status).map((a) => (
            <Button key={a.key} size="sm" className="mr-1 my-1" color={a.key === "confirm" ? "success" : a.key === "reject" ? "danger" : "secondary"} outline={a.key !== "confirm"} onClick={() => act(b, a)}>
              {t(`agenda.actions.${a.key}`)}
            </Button>
          ))}
          <Button size="sm" color="info" outline className="my-1" onClick={() => setModal({ booking: b })}>{t("agenda.actions.edit")}</Button>
        </Col>
      </Row>
    );
  };

  // ---------- agenda del giorno ----------
  const dayView = () => {
    const closedBox = data.closed && (
      <div className="alert alert-secondary py-2">
        {t("agenda.closedDay")} <Link to={SETTINGS_PATH}>{t("agenda.changeInSettings")}</Link>
      </div>
    );
    if (data.closed && data.results.length === 0) return closedBox;
    return (
      <>
        <div className="mb-2">
          <Button size="sm" className="mr-1 mb-1" color="primary" outline={bandFilter !== "all"} onClick={() => setBand("all")}>{t("agenda.allBands")}</Button>
          {shifts.map((s) => (
            <Button key={s.id} size="sm" className="mr-1 mb-1" color="primary" outline={bandFilter !== s.id} onClick={() => setBand(s.id)}>{s.name}</Button>
          ))}
        </div>
        <div className="mb-3">
          {FILTERS.map((f) => (
            <Button key={f} size="sm" className="mr-1 mb-1" color="secondary" outline={filter !== f} onClick={() => setFilter(f)}>
              {t(f === "all" ? "agenda.allStatuses" : `status.${f}`)}
            </Button>
          ))}
        </div>
        {closedBox}
        <Card>
          <CardBody>
            {groups.length === 0 ? (
              <p className="text-muted mb-0">{t(filter === "all" ? "agenda.none" : "agenda.noneStatus")}</p>
            ) : groups.map((g) => (
              <div key={g.shift ? g.shift.id : "other"} className="mb-4">
                <h5 className="mb-1">
                  {g.shift
                    ? t("agenda.bandTitle", { name: g.shift.name, from: g.shift.start_time, to: g.shift.end_time, n: g.shift.covers, cap: g.shift.capacity })
                    : t("agenda.outOfBand")}
                </h5>
                {g.items.length === 0
                  ? <p className="small text-muted mb-0">{t(filter === "all" ? "agenda.none" : "agenda.noneStatus")}</p>
                  : g.items.map(row)}
              </div>
            ))}
          </CardBody>
        </Card>
      </>
    );
  };

  // ---------- settimana ----------
  const weekView = () =>
    week && (
      <Row className="mb-3">
        {week.days.map((d) => (
          <Col key={d.date} xs="6" md className="mb-2">
            <Card
              className={`mb-0 h-100 ${d.date === date ? "border-primary" : ""}`}
              style={{ cursor: "pointer", background: d.closed ? "rgba(0,0,0,0.06)" : undefined, opacity: d.closed ? 0.7 : 1 }}
              onClick={() => goDay(d.date)}
            >
              <CardBody className="p-2">
                <strong>{t(`days.${d.weekday}`)} {Number(d.date.slice(8))}</strong>
                {d.date === week.today && <span className="badge ml-1" style={{ background: GOLD, color: "#fff" }}>{t("agenda.todayTag")}</span>}
                {d.closed ? (
                  <div className="small text-muted">{t("agenda.closed")}</div>
                ) : (
                  <>
                    {d.shifts.map((s) => (
                      <div key={s.id} className="small mt-1">
                        {s.name} {s.covers}/{s.capacity}
                        <Bar covers={s.covers} capacity={s.capacity} />
                      </div>
                    ))}
                    {d.pending > 0 && <div className="small mt-1" style={{ color: ORANGE, fontWeight: 600 }}>{t("agenda.toConfirm", { count: d.pending })}</div>}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>
    );

  return (
    <>
      {/* intestazione: titolo, frecce, data, giorni rapidi, Settings, nuova prenotazione */}
      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
        <h3 className="mb-0 mr-2">{t("agenda.title")}</h3>
        <Button size="sm" className="m-0" onClick={() => setDate(addDays(date, -step))} aria-label={t("agenda.prev")}>‹</Button>
        <span style={{ position: "relative", display: "inline-block" }}>
          <span className="btn btn-sm btn-link m-0" style={{ textTransform: "none", fontWeight: 600 }}>{dateLabel} ▾</span>
          <input
            type="date"
            value={date}
            aria-label={t("agenda.pickDate")}
            onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { /* il browser apre il suo calendario */ } }}
            onChange={(e) => goDay(e.target.value)}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }}
          />
        </span>
        <Button size="sm" className="m-0" onClick={() => setDate(addDays(date, step))} aria-label={t("agenda.next")}>›</Button>
        <div className="d-flex flex-wrap" style={{ gap: 4 }}>
          {quick.map((q) => (
            <Button key={q.date} size="sm" className="m-0" color="primary" outline={!(mode === "day" && date === q.date)} onClick={() => goDay(q.date)}>
              {quickLabel(q)}{pendingDays[q.date] > 0 && <span style={{ color: ORANGE, marginLeft: 4 }} title={t("agenda.hasPending")}>•</span>}
            </Button>
          ))}
        </div>
        <div className="ml-auto d-flex" style={{ gap: 4 }}>
          <Button size="sm" className="m-0" onClick={() => navigate(SETTINGS_PATH)}>{t("agenda.settings")}</Button>
          <Button size="sm" className="m-0" color="primary" onClick={() => setModal({ booking: null })}>{t("agenda.new")}</Button>
        </div>
      </div>

      {banner()}
      {tiles}

      <ButtonGroup size="sm" className="mb-3">
        <Button color="primary" outline={mode !== "day"} onClick={() => setMode("day")}>{t("agenda.dayView")}</Button>
        <Button color="primary" outline={mode !== "week"} onClick={() => setMode("week")}>{t("agenda.week")}</Button>
      </ButtonGroup>

      {mode === "week" ? weekView() : dayView()}

      <BookingModal
        isOpen={!!modal}
        booking={modal && modal.booking}
        date={date}
        channels={data.channels || []}
        tableGroups={groupsOfTables}
        sentText={sentText}
        onClose={() => setModal(null)}
        onSaved={(saved) => {
          setModal(null);
          setMode("day");
          if (saved && saved.date && saved.date !== date) {
            setDate(saved.date); // l'agenda si apre sul giorno della prenotazione
            loadPending();
            notifyChanged();
          } else changed();
        }}
      />
    </>
  );
};

export default Agenda;
